import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send, Building, Globe, MessageSquare } from 'lucide-react';
import './ContactFormSection.css';

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const validateForm = () => {
    let newErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) newErrors.name = "Please enter your full name";
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!emailPattern.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.subject.trim()) newErrors.subject = "Please specify your subject";
    if (!formData.message.trim()) newErrors.message = "Please provide your message";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setSuccessMessage("Your message has been successfully delivered. We'll respond within 24 business hours.");
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
      
      setTimeout(() => setSuccessMessage(""), 5000);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: <Building className="conto-contact-icon" />,
      title: "Corporate Headquarters",
      details: ["128 City Road, London", "EC1V 2NX, United Kingdom"],
      subtext: "Visit our central office",
      accentColor: "#b73235"
    },
    {
      icon: <Globe className="conto-contact-icon" />,
      title: "Digital Presence",
      details: ["support@devopstrioglobal.com", "support@devopstrio.co.uk"],
      subtext: "Typically respond within 2 hours",
      accentColor: "#2a6ecc"
    },
    {
      icon: <Phone className="conto-contact-icon" />,
      title: "Direct Contact",
      details: ["+44 1784 64 0216", "020 7996 223"],
      subtext: "Mon-Fri: 9:00 AM - 6:00 PM",
      accentColor: "#2a9d8f"
    }
  ];

  return (
    <section className="conto-section">
      <div className="conto-container">
        <div className="conto-content-area">
          <div className="conto-header-content">
            <div className="conto-prelude">
              <div className="conto-prelude-line"></div>
              <span className="conto-prelude-text">CONNECT WITH OUR TEAM</span>
            </div>
            <h1 className="conto-main-headline">
              {/* <span className="conto-headline-accent">Strategic</span> */}
              <span className="conto-headline-main">Partnerships</span>
              <span className="conto-headline-sub">Begin Here</span>
            </h1>
            <p className="conto-description">
              Engage with our expert consultants for tailored DevOps solutions. 
              Whether you're starting a new project or optimizing existing infrastructure, 
              we're here to architect your success.
            </p>
          </div>

          <div className="conto-connector">
            <div className="conto-connector-line"></div>
            <MessageSquare className="conto-connector-icon" />
            <div className="conto-connector-line"></div>
          </div>

          <div className="conto-contact-grid">
            {contactInfo.map((item, index) => (
              <div key={index} className="conto-contact-card">
                <div className="conto-card-ornament" style={{ backgroundColor: item.accentColor }}></div>
                <div className="conto-icon-frame" style={{ borderColor: item.accentColor }}>
                  {item.icon}
                </div>
                <div className="conto-card-content">
                  <h3 className="conto-card-title">{item.title}</h3>
                  <div className="conto-card-details">
                    {item.details.map((detail, idx) => (
                      <p key={idx} className="conto-detail-text">{detail}</p>
                    ))}
                  </div>
                  <p className="conto-card-subtext">{item.subtext}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="conto-form-area">
          <div className="conto-form-elevated">
            <div className="conto-form-header">
              <div className="conto-form-badge">NEW MESSAGE</div>
              <h2 className="conto-form-title">Project Inquiry Form</h2>
              <p className="conto-form-intro">
                Complete the form below for a comprehensive consultation. All fields marked 
                with * are required for expedited processing.
              </p>
            </div>

            {successMessage && (
              <div className="conto-success-state">
                <div className="conto-success-icon">
                  <div className="conto-success-check">✓</div>
                </div>
                <div className="conto-success-content">
                  <h4 className="conto-success-title">Message Received</h4>
                  <p className="conto-success-message">{successMessage}</p>
                </div>
              </div>
            )}

            <div className="conto-form-matrix">
              <div className="conto-field-group">
                <div className="conto-field-header">
                  <label className="conto-field-label">Full Name *</label>
                  {/* <span className="conto-field-indicator">Required</span> */}
                </div>
                <div className="conto-input-container">
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your complete name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`conto-input-field ${errors.name ? "conto-input-error" : ""}`}
                  />
                  <div className="conto-input-decoration"></div>
                </div>
                {errors.name && (
                  <div className="conto-error-state">
                    <span className="conto-error-icon">!</span>
                    <span className="conto-error-message">{errors.name}</span>
                  </div>
                )}
              </div>

              <div className="conto-field-group">
                <div className="conto-field-header">
                  <label className="conto-field-label">Business Email *</label>
                  {/* <span className="conto-field-indicator">Required</span> */}
                </div>
                <div className="conto-input-container">
                  <input
                    type="email"
                    name="email"
                    placeholder="company.name@domain.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={`conto-input-field ${errors.email ? "conto-input-error" : ""}`}
                  />
                  <div className="conto-input-decoration"></div>
                </div>
                {errors.email && (
                  <div className="conto-error-state">
                    <span className="conto-error-icon">!</span>
                    <span className="conto-error-message">{errors.email}</span>
                  </div>
                )}
              </div>

              <div className="conto-field-group conto-field-full">
                <div className="conto-field-header">
                  <label className="conto-field-label">Project Subject *</label>
                  {/* <span className="conto-field-indicator">Required</span> */}
                </div>
                <div className="conto-input-container">
                  <input
                    type="text"
                    name="subject"
                    placeholder="Brief description of your inquiry"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`conto-input-field ${errors.subject ? "conto-input-error" : ""}`}
                  />
                  <div className="conto-input-decoration"></div>
                </div>
                {errors.subject && (
                  <div className="conto-error-state">
                    <span className="conto-error-icon">!</span>
                    <span className="conto-error-message">{errors.subject}</span>
                  </div>
                )}
              </div>

              <div className="conto-field-group conto-field-full">
                <div className="conto-field-header">
                  <label className="conto-field-label">Detailed Message *</label>
                  {/* <span className="conto-field-indicator">Required</span> */}
                </div>
                <div className="conto-textarea-container">
                  <textarea
                    name="message"
                    placeholder="Describe your project requirements, challenges, and objectives..."
                    value={formData.message}
                    onChange={handleChange}
                    className={`conto-textarea-field ${errors.message ? "conto-input-error" : ""}`}
                    rows="6"
                  />
                  <div className="conto-textarea-decoration"></div>
                </div>
                {errors.message && (
                  <div className="conto-error-state">
                    <span className="conto-error-icon">!</span>
                    <span className="conto-error-message">{errors.message}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="conto-submit-area">
              <button 
                onClick={handleSubmit} 
                className="conto-submit-primary"
                disabled={isSubmitting}
              >
                <span className="conto-submit-text">
                  {isSubmitting ? (
                    <>
                      <span className="conto-loading-spinner"></span>
                      Processing Submission...
                    </>
                  ) : (
                    "Initiate Consultation"
                  )}
                </span>
                {!isSubmitting && <Send className="conto-submit-icon" />}
              </button>
              <p className="conto-submit-note">
                By submitting, you agree to our privacy policy and consent to contact.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;